import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, ease as mmEase } from "@/lib/motion";
import { ArrowRight, Eye, EyeOff, CheckCircle2, User, Mail, Phone, Globe2, Lock, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Turnstile from "@/components/Turnstile";
import AuthThreadMini from "@/components/auth/AuthThreadMini";
import { useAuth } from "@/context/AuthContext";
import { COUNTRIES } from "@/lib/constants";

const ez = mmEase.expo;
const fv = (delay) => ({
  initial: { opacity: 0, x: 16, filter: "blur(3px)" },
  animate: { opacity: 1, x: 0,  filter: "blur(0px)" },
  transition: { duration: 0.48, ease: ez, delay },
});

// Matches the backend's RegisterRequest.phone validator (models.py) —
// lightweight, not a strict per-country format: allows the common
// formatting characters and just checks the digit count falls in the
// general international range.
const PHONE_ALLOWED_CHARS = /^[+\d\s\-().]+$/;
const isValidPhone = (v) => {
  const digits = (v.match(/\d/g) || []).length;
  return PHONE_ALLOWED_CHARS.test(v) && digits >= 7 && digits <= 15;
};

const PERKS = [
  "Hold 7 currencies simultaneously",
  "See your exchange rate and fee upfront, before you send",
  "Transfers deliver in under 3 minutes",
  "Bank-grade security, zero monthly fees",
];

const TRUST = [
  { icon: ShieldCheck, label: "KYC & AML Screening" },
  { icon: Lock, label: "Bank-grade Encryption" },
];

export default function Register() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", country: "GB", password: "" });
  const [show, setShow]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const pwStrength = (() => {
    const p = form.password;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();
  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][pwStrength];
  const strengthColor = ["", "#EF4444", "#F59E0B", "#06B6D4", "#10B981"][pwStrength];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (form.phone.trim() && !isValidPhone(form.phone.trim())) {
      toast.error("Enter a valid phone number");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, captcha_token: captchaToken });
      toast.success("Welcome to MoneyMeet — your wallet is ready.");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left — brand panel ───────────────────────────────── */}
      <div className="hidden lg:flex w-[440px] xl:w-[500px] flex-col hero-gradient grain relative overflow-hidden">
        <div
          className="absolute top-[25%] left-[10%] w-72 h-72 rounded-full blur-3xl animate-orb pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 70%)" }}
        />
        <div
          className="absolute bottom-[20%] right-[5%] w-56 h-56 rounded-full blur-3xl animate-orb-b pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(124,58,237,0.12) 0%, transparent 70%)" }}
        />

        <div className="relative z-10 p-10 flex flex-col h-full">
          <Link to="/"><Logo variant="dark" size="md" /></Link>

          <div className="flex-1 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: ez }}
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-4">
                Join MoneyMeet
              </p>
              <h1
                className="font-display font-black text-white leading-[1.08] tracking-tight mb-5 text-balance"
                style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", letterSpacing: "-0.04em" }}
              >
                A wallet that<br />crosses oceans.
              </h1>
              <p className="text-white/50 text-[14px] leading-relaxed max-w-sm mb-8">
                Open your free multi-currency wallet in 60 seconds. Start sending money to family in minutes.
              </p>
            </motion.div>

            <motion.ul
              className="space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.22, ease: ez }}
            >
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <CheckCircle2 size={15} className="text-emerald shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-[13px] text-white/65">{p}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              className="mt-9 flex items-center gap-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.38, ease: ez }}
            >
              <div className="flex -space-x-2">
                {["AO", "KA", "FH", "ED"].map((i, idx) => (
                  <div
                    key={i}
                    className="w-7 h-7 rounded-full border-2 border-navy/80 grid place-items-center text-[9px] font-bold text-white"
                    style={{ background: ["#10B981", "#06B6D4", "#7C3AED", "#F59E0B"][idx] }}
                  >
                    {i}
                  </div>
                ))}
              </div>
              <p className="text-[12px] text-white/45">
                Joined by <span className="text-white/70 font-semibold">80,000+</span> people worldwide
              </p>
            </motion.div>

            {/* The Thread */}
            <motion.div
              className="mt-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.52, ease: ez }}
            >
              <AuthThreadMini color="#06B6D4" />
            </motion.div>
          </div>

          {/* Trust signals */}
          <motion.div
            className="flex items-center gap-5 mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.60, ease: ez }}
          >
            {TRUST.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-1.5">
                <Icon size={12} className="text-emerald" strokeWidth={2.5} />
                <span className="text-[10px] font-semibold text-white/38">{label}</span>
              </div>
            ))}
          </motion.div>

          <p className="text-white/22 text-[11px]">© {new Date().getFullYear()} MoneyMeet Pay</p>
        </div>
      </div>

      {/* ── Right — form ─────────────────────────────────────── */}
      <div className="flex-1 flex flex-col bg-white">
        <div className="px-5 sm:px-8 py-5 flex items-center justify-between border-b border-border/50">
          <div className="lg:hidden"><Link to="/"><Logo variant="light" size="sm" /></Link></div>
          <div className="hidden lg:block" />
          <Link
            to="/login"
            data-testid="register-signin-link"
            className="text-[13px] text-muted-foreground hover:text-navy transition-colors"
          >
            Already have an account?{" "}
            <span className="text-emerald font-semibold">Sign in →</span>
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 py-10">
          <div className="w-full max-w-[420px]">
            <motion.div {...fv(0)}>
              <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-2">
                Create account
              </p>
              <h2
                className="font-display font-black text-navy tracking-tight mb-1"
                style={{ fontSize: "1.65rem", letterSpacing: "-0.035em" }}
              >
                Open your wallet
              </h2>
              <p className="text-sm text-muted-foreground mb-7">
                Completely free · No monthly fees · Takes 60 seconds
              </p>
            </motion.div>

            <form onSubmit={onSubmit} data-testid="register-form" className="space-y-3.5">
              <motion.div {...fv(0.06)}>
                <label className="input-label" htmlFor="rg-name">Full name</label>
                <div className="relative">
                  <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="rg-name"
                    required
                    data-testid="register-name-input"
                    value={form.full_name}
                    onChange={set("full_name")}
                    placeholder="Ada Eze"
                    className="input-field pl-9"
                  />
                </div>
              </motion.div>

              <motion.div {...fv(0.10)}>
                <label className="input-label" htmlFor="rg-email">Email address</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="rg-email"
                    type="email"
                    required
                    data-testid="register-email-input"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@email.com"
                    className="input-field pl-9"
                  />
                </div>
              </motion.div>

              <div className="grid grid-cols-2 gap-3">
                <motion.div {...fv(0.14)}>
                  <label className="input-label" htmlFor="rg-phone">Phone</label>
                  <div className="relative">
                    <Phone size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                      id="rg-phone"
                      type="tel"
                      data-testid="register-phone-input"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+44 7700…"
                      className="input-field pl-9"
                    />
                  </div>
                </motion.div>
                <motion.div {...fv(0.16)}>
                  <label className="input-label" htmlFor="rg-country">Country</label>
                  <div className="relative">
                    <Globe2 size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
                    <select
                      id="rg-country"
                      data-testid="register-country-select"
                      value={form.country}
                      onChange={set("country")}
                      className="input-field pl-9 text-navy appearance-none cursor-pointer"
                    >
                      {COUNTRIES.map((c) => (
                        <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
                      ))}
                    </select>
                  </div>
                </motion.div>
              </div>

              <motion.div {...fv(0.20)}>
                <label className="input-label" htmlFor="rg-password">Password</label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                  <input
                    id="rg-password"
                    type={show ? "text" : "password"}
                    required
                    data-testid="register-password-input"
                    value={form.password}
                    onChange={set("password")}
                    placeholder="At least 8 characters"
                    className="input-field pl-9 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((p) => !p)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-navy transition-colors"
                    aria-label={show ? "Hide password" : "Show password"}
                  >
                    {show ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {form.password.length >= 4 && (
                  <div className="mt-2 flex items-center gap-2" role="status" aria-label={`Password strength: ${strengthLabel}`}>
                    <div className="flex gap-1 flex-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="h-1 flex-1 rounded-full transition-colors duration-300"
                          style={{ background: pwStrength >= i ? strengthColor : "#E2E8F0" }}
                        />
                      ))}
                    </div>
                    <span className="text-[11px] font-semibold" style={{ color: strengthColor }}>
                      {strengthLabel}
                    </span>
                  </div>
                )}
              </motion.div>

              <Turnstile onVerify={setCaptchaToken} />

              <motion.div {...fv(0.26)}>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-button"
                  className="btn-primary w-full justify-center py-3.5 text-sm mt-1 disabled:opacity-55 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/25 border-t-white rounded-full animate-spin" />
                      Creating your wallet…
                    </span>
                  ) : (
                    <>Create my wallet <ArrowRight size={14} /></>
                  )}
                </button>
              </motion.div>
            </form>

            <motion.p
              {...fv(0.32)}
              className="text-center text-[11px] text-muted-foreground mt-5 leading-relaxed"
            >
              By continuing you agree to MoneyMeet's{" "}
              <Link to="/legal/terms" className="text-navy/70 hover:text-navy underline underline-offset-2">Terms</Link>
              {" "}and{" "}
              <Link to="/legal/privacy" className="text-navy/70 hover:text-navy underline underline-offset-2">Privacy Notice</Link>.
            </motion.p>

            <motion.div
              {...fv(0.38)}
              className="mt-5 p-4 border border-border/60 rounded-2xl text-center bg-secondary/30"
            >
              <p className="text-[12px] text-muted-foreground">
                Registering for a business?{" "}
                <Link
                  to="/register/business"
                  data-testid="register-business-link"
                  className="font-bold text-emerald hover:text-emerald-hover transition-colors"
                >
                  Open a business account →
                </Link>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
